/**
 * Language Store (Zustand)
 * Hanterar språkval och översättningar
 */

import { create } from 'zustand';
import i18n from '../i18n/config';

export type Language = 'sv' | 'en' | 'lt';

const SUPPORTED_LANGUAGES: readonly Language[] = ['sv', 'en', 'lt'];

interface LanguageState {
  language: Language;
  setLanguage: (lang: Language) => void;
  initializeLanguage: (lang: Language) => void;
}

function resolveLanguage(value: string | null | undefined): Language {
  const base = (value ?? '').toLowerCase().split('-')[0];
  if ((SUPPORTED_LANGUAGES as readonly string[]).includes(base)) {
    return base as Language;
  }
  return 'sv';
}

function applyDocumentLanguage(lang: Language): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
}

const initialLanguage = resolveLanguage(localStorage.getItem('i18nextLng'));
applyDocumentLanguage(initialLanguage);

export const useLanguageStore = create<LanguageState>((set) => ({
  language: initialLanguage,

  setLanguage: async (lang: Language) => {
    const next = resolveLanguage(lang);
    try {
      await i18n.changeLanguage(next);
      localStorage.setItem('i18nextLng', next);
      applyDocumentLanguage(next);
      set({ language: next });
      // Force re-render av alla komponenter som använder useTranslation
      // react-i18next triggar automatiskt re-renders via sin hook
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  },

  initializeLanguage: async (lang: Language) => {
    // Används när användaren loggar in för att sätta språk från user.preferred_language
    const next = resolveLanguage(lang);
    try {
      console.log('Initializing language to:', next);
      await i18n.changeLanguage(next);
      localStorage.setItem('i18nextLng', next);
      applyDocumentLanguage(next);
      set({ language: next });
      console.log('Language initialized successfully. Current i18n language:', i18n.language);
    } catch (error) {
      console.error('Failed to initialize language:', error);
    }
  },
}));

// Re-export useTranslation från react-i18next för enkel användning
export { useTranslation } from 'react-i18next';

// Hook för att få språkval och ändra språk
export const useLanguage = () => {
  const language = useLanguageStore((state) => state.language);
  const setLanguage = useLanguageStore((state) => state.setLanguage);
  const initializeLanguage = useLanguageStore((state) => state.initializeLanguage);
  
  return { language, setLanguage, initializeLanguage };
};
